import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {jwtDecode} from 'jwt-decode';
import get from 'lodash/get';
import set from 'lodash/set';
import {RootState} from '../../app/store';
import {Session} from './types';
import {refreshTokenFulfilled, refreshTokenRejected} from '../../app/services/api';
import {identitiesApi} from '../../app/services/identitiesApi';

export const DEFAULT_TENANT_ID = 1;

export interface AuthState {
  session: Session | null;
  sessionData: {[key: string]: {[key: string]: unknown}};
  tenantId: number;
  token: string | null;
}

const initialState: AuthState = {
  session: null,
  sessionData: {},
  tenantId: DEFAULT_TENANT_ID,
  token: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setSessionData: (state, action: PayloadAction<{key: string; value: unknown}>) => {
      if (state.session) {
        set(state.sessionData, `${state.session.id}.${action.payload.key}`, action.payload.value);
        state.sessionData = {...state.sessionData};
      }
    },
    logout: (state) => {
      updateToken(state, null);
    },
    setTenantId: (state, action: PayloadAction<number>) => {
      state.tenantId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(refreshTokenFulfilled, (state, {payload: {token}}) => {
      updateToken(state, token);
    });
    builder.addCase(refreshTokenRejected, (state) => {
      updateToken(state, null);
    });
    builder.addMatcher(identitiesApi.endpoints.login.matchFulfilled, (state, {payload: {token}}) => {
      updateToken(state, token);
    });
    builder.addMatcher(identitiesApi.endpoints.loginWithToken.matchFulfilled, (state, {payload: {token}}) => {
      updateToken(state, token);
    });
    builder.addMatcher(identitiesApi.endpoints.startDemo.matchFulfilled, (state, {payload: {token}}) => {
      updateToken(state, token);
    });
    builder.addMatcher(identitiesApi.endpoints.modifyName.matchFulfilled, (state, {meta: {arg}}) => {
      if (state.session) {
        state.session.name = arg.originalArgs.name;
      }
    });
    builder.addMatcher(identitiesApi.endpoints.modifyPassword.matchFulfilled, (state, {meta: {arg}}) => {
      if (arg.originalArgs.currentPassword != null && arg.originalArgs.currentPassword != '*') {
        updateToken(state, null);
      }
    });
    builder.addMatcher(identitiesApi.endpoints.refreshToken.matchFulfilled, (state, {payload: {token}}) => {
      updateToken(state, token);
    });
    builder.addMatcher(identitiesApi.endpoints.registerVerifyOTP.matchFulfilled, (state, {payload: {token}}) => {
      updateToken(state, token);
    });
  },
});

function updateToken(state: AuthState, token: string | null) {
  if (token == null) {
    state.session = null;
    state.tenantId = DEFAULT_TENANT_ID;
    state.token = null;
  } else {
    state.session = jwtDecode<Session>(token);
    state.tenantId = state.session.tenantIds?.split(',').map(Number)[0] || DEFAULT_TENANT_ID;
    state.token = token;
  }
}

export const {logout, setSessionData, setTenantId} = authSlice.actions;

export const selectSession = (state: RootState) => state.auth.session;

export const selectSessionData = (state: RootState) =>
  state.auth.session == null ? null : (get(state.auth.sessionData, `${state.auth.session.id}`) ?? null);

export const selectCurrentToken = (state: RootState) => state.auth.token;

export const selectTenantId = (state: RootState) => state.auth.tenantId;

export default authSlice.reducer;
